/**
 * React Query Configuration
 * Coláiste Lurgan Management System - API state management
 */

import { QueryClient } from '@tanstack/react-query';

// React Query Configuration
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Global query defaults
      staleTime: 5 * 60 * 1000, // 5 minutes - data considered fresh
      cacheTime: 10 * 60 * 1000, // 10 minutes - cache retention
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors except 408 (timeout)
        if (error?.response?.status >= 400 && error?.response?.status < 500) {
          if (error?.response?.status === 408) return failureCount < 3;
          return false;
        }
        // Retry up to 3 times for other errors
        return failureCount < 3;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
      refetchOnWindowFocus: false, // Prevent excessive refetching
      refetchOnMount: true,
      refetchOnReconnect: 'always',
    },
    mutations: {
      // Global mutation defaults
      retry: (failureCount, error) => {
        // Don't retry mutations on 4xx errors
        if (error?.response?.status >= 400 && error?.response?.status < 500) {
          return false;
        }
        // Retry up to 2 times for server errors
        return failureCount < 2;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
    },
  },
});

// Query Keys Factory - Consistent key management
export const queryKeys = {
  // Authentication
  auth: {
    profile: () => ['auth', 'profile'],
    permissions: () => ['auth', 'permissions'],
  },

  // Students
  students: {
    all: () => ['students'],
    lists: () => [...queryKeys.students.all(), 'list'],
    list: (filters) => [...queryKeys.students.lists(), { filters }],
    details: () => [...queryKeys.students.all(), 'detail'],
    detail: (id) => [...queryKeys.students.details(), id],
    analytics: () => [...queryKeys.students.all(), 'analytics'],
    // Infinite queries
    infinite: (filters) => [...queryKeys.students.all(), 'infinite', { filters }],
  },

  // Courses
  courses: {
    all: () => ['courses'],
    lists: () => [...queryKeys.courses.all(), 'list'],
    list: (filters) => [...queryKeys.courses.lists(), { filters }],
    details: () => [...queryKeys.courses.all(), 'detail'],
    detail: (id) => [...queryKeys.courses.details(), id],
    enrollment: (id) => [...queryKeys.courses.detail(id), 'enrollment'],
    analytics: () => [...queryKeys.courses.all(), 'analytics'],
  },

  // Houses
  houses: {
    all: () => ['houses'],
    lists: () => [...queryKeys.houses.all(), 'list'],
    list: (filters) => [...queryKeys.houses.lists(), { filters }],
    details: () => [...queryKeys.houses.all(), 'detail'],
    detail: (uuid) => [...queryKeys.houses.details(), uuid],
    occupancy: (uuid) => [...queryKeys.houses.detail(uuid), 'occupancy'],
    capacityMatrix: () => [...queryKeys.houses.all(), 'capacity-matrix'],
  },

  // Classes
  classes: {
    all: () => ['classes'],
    lists: () => [...queryKeys.classes.all(), 'list'],
    list: (filters) => [...queryKeys.classes.lists(), { filters }],
    details: () => [...queryKeys.classes.all(), 'detail'],
    detail: (id) => [...queryKeys.classes.details(), id],
  },

  // Support data (rarely changes)
  schoolYears: {
    all: () => ['school-years'],
  },

  travelLocations: {
    all: () => ['travel-locations'],
  },

  // Payments
  payments: {
    all: () => ['payments'],
    lists: () => [...queryKeys.payments.all(), 'list'],
    list: (filters) => [...queryKeys.payments.lists(), { filters }],
    details: () => [...queryKeys.payments.all(), 'detail'],
    detail: (id) => [...queryKeys.payments.details(), id],
    student: (studentUuid) => ['payments', 'student', studentUuid],
    financialSummary: () => [...queryKeys.payments.all(), 'financial_summary'],
    studentPaymentSummaries: () => [...queryKeys.payments.all(), 'student_payment_summaries'],
  },

  // Audit Log
  auditLog: {
    all: () => ['audit-log'],
    lists: () => [...queryKeys.auditLog.all(), 'list'],
    list: (filters) => [...queryKeys.auditLog.lists(), { filters }],
  },

  // Dashboard Analytics
  dashboard: {
    all: () => ['dashboard-analytics'],
    analytics: (courseFilter) => ['dashboard-analytics', courseFilter],
  },
};

// Cache invalidation helpers
export const invalidateQueries = {
  // Invalidate all student-related queries
  students: () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.students.all() });
  },

  // Invalidate specific student
  student: (id) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.students.detail(id) });
    queryClient.invalidateQueries({ queryKey: queryKeys.students.lists() });
    queryClient.invalidateQueries({ queryKey: queryKeys.students.analytics() });
  },

  // Invalidate courses
  courses: () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.courses.all() });
  },

  // Invalidate specific course
  course: (id) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.courses.detail(id) });
    queryClient.invalidateQueries({ queryKey: queryKeys.courses.lists() });
    queryClient.invalidateQueries({ queryKey: queryKeys.courses.analytics() });
  },

  // Invalidate allocation board for a course
  allocation: (courseUuid) => {
    queryClient.invalidateQueries({ queryKey: ['allocation', courseUuid] });
  },

  // Invalidate all house data (list + matrix)
  houses: () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.houses.all() });
  },

  // Invalidate specific house
  house: (uuid) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.houses.detail(uuid) });
    queryClient.invalidateQueries({ queryKey: queryKeys.houses.lists() });
  },

  // Invalidate capacity matrix only
  houseMatrix: () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.houses.capacityMatrix() });
  },

  // Invalidate classes
  classes: () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.classes.all() });
  },

  // Invalidate friendships
  friendships: () => {
    queryClient.invalidateQueries({ queryKey: ['friendships'] });
  },

  // Invalidate all payments
  payments: () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.payments.all() });
  },

  // Invalidate specific student's payments
  studentPayments: (studentUuid) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.payments.student(studentUuid) });
    queryClient.invalidateQueries({ queryKey: queryKeys.payments.lists() });
  },

  // Invalidate auth data
  auth: () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.auth.profile() });
    queryClient.invalidateQueries({ queryKey: queryKeys.auth.permissions() });
  },

  // Invalidate dashboard analytics
  dashboard: () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all() });
  },

  // Clear all cache (use carefully)
  all: () => {
    queryClient.clear();
  },
};

// Optimistic update helpers
export const optimisticUpdates = {
  // Update student in cache optimistically
  updateStudent: (studentId, newData) => {
    queryClient.setQueryData(queryKeys.students.detail(studentId), (oldData) => ({
      ...oldData,
      ...newData,
    }));

    // Update in lists as well (match by uuid OR id for compatibility)
    queryClient.setQueriesData(
      { queryKey: queryKeys.students.lists(), type: 'active' },
      (oldData) => {
        if (!oldData?.results) return oldData;

        return {
          ...oldData,
          results: oldData.results.map((student) =>
            (student.uuid === studentId || student.id === studentId) ? { ...student, ...newData } : student
          ),
        };
      }
    );
  },

  // Add new student to cache optimistically
  addStudent: (newStudent) => {
    queryClient.setQueriesData(
      { queryKey: queryKeys.students.lists(), type: 'active' },
      (oldData) => {
        if (!oldData?.results) return oldData;
        
        return {
          ...oldData,
          results: [newStudent, ...oldData.results],
          count: oldData.count + 1,
        };
      }
    );
  },

  // Remove student from cache optimistically
  removeStudent: (studentId) => {
    queryClient.setQueriesData(
      { queryKey: queryKeys.students.lists(), type: 'active' },
      (oldData) => {
        if (!oldData?.results) return oldData;

        return {
          ...oldData,
          results: oldData.results.filter((student) => student.uuid !== studentId && student.id !== studentId),
          count: Math.max(0, oldData.count - 1),
        };
      }
    );
  },
};

export default queryClient;